import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { DEFAULT_SETTINGS } from '../data/scoring';
import { UNIQUE_PLAYERS } from '../data/players';
import { filterPlayerList } from '../utils/filterPlayerList';

// ── Per-league targets ──────────────────────────────────────────────────────
// Draft-board targets are league-specific ("who I'm chasing in THIS league"),
// so they're stored under a per-league key, mirroring the per-league opponent
// configs and mock watchlist. The pre-league global key is read once as a
// copy-forward seed — never written to, never deleted.
const LEGACY_TARGETS_KEY = "auctionArchitect_myTargets";
const tgtKey = lg => "auctionArchitect_myTargets_" + (lg || "default");

function loadMyTargets(league) {
  try {
    const own = JSON.parse(localStorage.getItem(tgtKey(league)));
    if (Array.isArray(own)) return new Set(own);
  } catch (e) { /* ignore */ }
  try {
    const legacy = JSON.parse(localStorage.getItem(LEGACY_TARGETS_KEY));
    if (Array.isArray(legacy)) return new Set(legacy);
  } catch (e) { /* ignore */ }
  return new Set();
}

export default function useDraftBoard(settings, pricing, rosterSlots, activeLeagueName = null) {
  const { getPlayerValue, getPlayerVorp } = pricing;

  // ── State ─────────────────────────────────────────────────────────────────
  const [draftTeams, setDraftTeams] = useState(() => {
    const teams = [];
    for (let i = 0; i < DEFAULT_SETTINGS.teams; i++) {
      teams.push({ name: i === 0 ? "Ben" : `Team ${i + 1}`, id: i });
    }
    return teams;
  });
  const [draftPicks, setDraftPicks] = useState([]);
  const [draftingPlayer, setDraftingPlayer] = useState(null);
  const [draftPrice, setDraftPrice] = useState("");
  const [draftTeamId, setDraftTeamId] = useState(null);
  const [draftNominatorId, setDraftNominatorId] = useState(0);
  const [showDraftRecap, setShowDraftRecap] = useState(false);
  const [draftSearch, setDraftSearch] = useState("");
  const [draftPosFilter, setDraftPosFilter] = useState(new Set(["ALL"]));
  const [editingTeamName, setEditingTeamName] = useState(null);
  const [draftMobileTab, setDraftMobileTab] = useState("players");
  const [myTargets, setMyTargets] = useState(() => loadMyTargets(activeLeagueName));
  const [assistantTab, setAssistantTab] = useState("assistant");

  // Which league the in-memory myTargets belong to. The persist effect writes to
  // THIS league's key, not activeLeagueName's — on a league switch the state
  // update and the name change land in the same commit, and writing to the new
  // key before the load lands would copy the OLD league's targets into it.
  const targetsLeagueRef = useRef(activeLeagueName);

  // ── Effects ───────────────────────────────────────────────────────────────
  // Sync draft teams when settings.teams changes
  useEffect(() => {
    setDraftTeams(prev => {
      const newTeams = [];
      for (let i = 0; i < settings.teams; i++) {
        newTeams.push(prev[i] || { name: `Team ${i + 1}`, id: i });
      }
      return newTeams;
    });
  }, [settings.teams]);

  // Swap in the new league's targets when the active league changes. Targets are
  // just a Set of names — no team-count interaction — so unlike the opponent
  // configs this needs no guard against the settings.teams effect above.
  useEffect(() => {
    if (targetsLeagueRef.current === activeLeagueName) return;
    // Claim the ref FIRST — the persist effect keys off it, so the Set we're
    // about to set must already be attributed to the incoming league.
    targetsLeagueRef.current = activeLeagueName;
    setMyTargets(loadMyTargets(activeLeagueName));
  }, [activeLeagueName]);

  useEffect(() => {
    localStorage.setItem(tgtKey(targetsLeagueRef.current), JSON.stringify([...myTargets]));
  }, [myTargets]);

  // ── Computed ──────────────────────────────────────────────────────────────
  const BUDGET = settings.budget;

  const draftedPlayerNames = useMemo(() => new Set(draftPicks.map(p => p.player.name)), [draftPicks]);

  const teamRosters = useMemo(() => {
    const rosters = {};
    draftTeams.forEach(t => { rosters[t.id] = []; });
    draftPicks.forEach(pick => {
      if (rosters[pick.teamId]) rosters[pick.teamId].push(pick);
    });
    return rosters;
  }, [draftPicks, draftTeams]);

  const teamBudgets = useMemo(() => {
    const budgets = {};
    draftTeams.forEach(t => {
      const s = (teamRosters[t.id] || []).reduce((sum, pick) => sum + pick.price, 0);
      const filled = (teamRosters[t.id] || []).length;
      budgets[t.id] = { spent: s, remaining: BUDGET - s, filled, total: rosterSlots.length };
    });
    return budgets;
  }, [draftTeams, teamRosters, BUDGET, rosterSlots]);

  const draftFilteredPlayers = useMemo(() => {
    const available = UNIQUE_PLAYERS.filter(p => !draftedPlayerNames.has(p.name));
    const list = filterPlayerList(available, draftPosFilter, draftSearch);
    list.sort((a, b) => getPlayerVorp(b) - getPlayerVorp(a));
    return list;
  }, [draftPosFilter, draftSearch, draftedPlayerNames, getPlayerVorp]);

  const teamMaxBids = useMemo(() => {
    const maxBids = {};
    draftTeams.forEach(t => {
      const b = teamBudgets[t.id];
      if (!b) return;
      const emptySlots = b.total - b.filled;
      maxBids[t.id] = emptySlots <= 1 ? b.remaining : b.remaining - (emptySlots - 1);
    });
    return maxBids;
  }, [draftTeams, teamBudgets]);

  const marketTrends = useMemo(() => {
    if (draftPicks.length === 0) return null;
    const byPos = {};
    let totalValue = 0, totalPrice = 0;
    draftPicks.forEach(pick => {
      const val = getPlayerValue(pick.player);
      totalValue += val;
      totalPrice += pick.price;
      if (!byPos[pick.player.pos]) byPos[pick.player.pos] = { value: 0, price: 0, count: 0 };
      byPos[pick.player.pos].value += val;
      byPos[pick.player.pos].price += pick.price;
      byPos[pick.player.pos].count += 1;
    });
    const overall = totalValue > 0 ? ((totalPrice - totalValue) / totalValue * 100) : 0;
    const positions = {};
    for (const [pos, data] of Object.entries(byPos)) {
      positions[pos] = { pct: data.value > 0 ? ((data.price - data.value) / data.value * 100) : 0, count: data.count };
    }
    return { overall, positions, totalValue, totalPrice };
  }, [draftPicks, getPlayerValue]);

  const teamNeeds = useMemo(() => {
    const needs = {};
    draftTeams.forEach(t => {
      const picks = teamRosters[t.id] || [];
      const filled = {};
      picks.forEach(p => { filled[p.player.pos] = (filled[p.player.pos] || 0) + 1; });
      const required = {};
      for (const [pos, count] of Object.entries(settings.positions)) {
        if (pos === "FLEX" || pos === "SFLEX" || pos === "BN") continue;
        const have = filled[pos] || 0;
        if (have < count) required[pos] = count - have;
      }
      needs[t.id] = required;
    });
    return needs;
  }, [draftTeams, teamRosters, settings.positions]);

  const nominationSuggestions = useMemo(() => {
    const suggestions = [];
    const myTeamId = 0;
    const posNeedCounts = {};
    draftTeams.forEach(t => {
      if (t.id === myTeamId) return;
      const need = teamNeeds[t.id] || {};
      const maxBid = teamMaxBids[t.id] || 0;
      for (const pos of Object.keys(need)) {
        if (!posNeedCounts[pos]) posNeedCounts[pos] = [];
        if (maxBid > 5) posNeedCounts[pos].push(t);
      }
    });
    for (const [pos, teams] of Object.entries(posNeedCounts)) {
      if (teams.length >= 2) {
        const topPlayer = UNIQUE_PLAYERS.filter(p => p.pos === pos && !draftedPlayerNames.has(p.name))
          .sort((a, b) => getPlayerVorp(b) - getPlayerVorp(a))[0];
        if (topPlayer) {
          const teamNames = teams.slice(0, 3).map(t => t.name).join(" & ");
          suggestions.push({ player: topPlayer, reason: `${teamNames} all need ${pos}`, teams });
        }
      }
    }
    return suggestions.slice(0, 4);
  }, [draftTeams, teamNeeds, teamMaxBids, draftedPlayerNames, getPlayerVorp]);

  const positionalScarcity = useMemo(() => {
    const positions = ["QB", "RB", "WR", "TE", "DEF", "K"];
    return positions.map(pos => {
      const available = UNIQUE_PLAYERS.filter(p => p.pos === pos && !draftedPlayerNames.has(p.name));
      const elite = available.filter(p => getPlayerVorp(p) >= 15).length;
      const startable = available.filter(p => getPlayerVorp(p) >= 5).length;
      return { pos, total: available.length, elite, startable };
    });
  }, [draftedPlayerNames, getPlayerVorp]);

  const targetStatus = useMemo(() => {
    const myMaxBid = teamMaxBids[0] || BUDGET;
    return [...myTargets].map(name => {
      const player = UNIQUE_PLAYERS.find(p => p.name === name);
      if (!player) return null;
      const drafted = draftedPlayerNames.has(name);
      const value = getPlayerValue(player);
      const unaffordable = !drafted && value > myMaxBid;
      return { player, drafted, value, unaffordable };
    }).filter(Boolean);
  }, [myTargets, draftedPlayerNames, getPlayerValue, teamMaxBids, BUDGET]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const startDraft = useCallback((player) => {
    setDraftingPlayer(player);
    // Pre-fill with the league-dynamic price (getPlayerValue = the dynamicPrices
    // lookup), not the legacy player.value.
    setDraftPrice(String(getPlayerValue(player)));
    setDraftTeamId(null);
  }, [getPlayerValue]);

  const confirmDraft = useCallback(() => {
    if (!draftingPlayer || draftTeamId === null) return;
    const price = parseInt(draftPrice);
    if (isNaN(price) || price < 0) return;
    setDraftPicks(prev => [...prev, { player: draftingPlayer, price, teamId: draftTeamId, nominatorId: draftNominatorId, timestamp: Date.now() }]);
    setDraftingPlayer(null);
    setDraftPrice("");
    setDraftTeamId(null);
    setDraftNominatorId(prev => (prev + 1) % draftTeams.length);
  }, [draftingPlayer, draftTeamId, draftPrice, draftNominatorId, draftTeams.length]);

  const undoLastPick = useCallback(() => setDraftPicks(prev => prev.slice(0, -1)), []);

  const renameTeam = useCallback((id, name) => {
    setDraftTeams(prev => prev.map(t => t.id === id ? { ...t, name } : t));
    setEditingTeamName(null);
  }, []);

  const resetDraft = useCallback(() => {
    setDraftPicks([]);
    setDraftingPlayer(null);
  }, []);

  const toggleTarget = useCallback((player) => {
    const key = player.name;
    setMyTargets(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  return {
    // State
    draftTeams, setDraftTeams,
    draftPicks, setDraftPicks,
    draftingPlayer, setDraftingPlayer,
    draftPrice, setDraftPrice,
    draftTeamId, setDraftTeamId,
    draftNominatorId, setDraftNominatorId,
    showDraftRecap, setShowDraftRecap,
    draftSearch, setDraftSearch,
    draftPosFilter, setDraftPosFilter,
    editingTeamName, setEditingTeamName,
    draftMobileTab, setDraftMobileTab,
    myTargets, setMyTargets,
    assistantTab, setAssistantTab,
    // Computed
    draftedPlayerNames,
    teamRosters, teamBudgets,
    draftFilteredPlayers,
    teamMaxBids, marketTrends,
    teamNeeds, nominationSuggestions,
    positionalScarcity, targetStatus,
    // Actions
    startDraft, confirmDraft,
    undoLastPick, renameTeam,
    resetDraft, toggleTarget,
  };
}
